import React, { useState, useEffect } from "react";
import config from "../../Config";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import { NavLink, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import Pagination from "../General/Pagination";
import LibraryMenu from "./LibraryMenu";
import Modal from "react-bootstrap/Modal";
import { Form } from "react-bootstrap";
import csvDownload from "json-to-csv-export";

const OverdueReport = () => {
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get("mode") === "outstanding" ? "outstanding" : "overdue";

  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));

  const [listOfRecords, setListOfRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [reportMode, setReportMode] = useState(initialMode);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [refreshToken, setRefreshToken] = useState(0);

  const [showReturn, setShowReturn] = useState(false);
  const [showRenew, setShowRenew] = useState(false);
  const [selectedItem, setSelectedItem] = useState({});
  const [returnDate, setReturnDate] = useState(moment().format(config.date_format_input));
  const [renewDate, setRenewDate] = useState("");
  const [estimatedFine, setEstimatedFine] = useState(0);

  const overdueOnly = reportMode === "overdue";

  useEffect(() => {
    document.title = overdueOnly ? "Overdue Books" : "Outstanding Books";
    setLoading(true);

    const data = {
      AccessKey: AccessKey,
      UserID: UserID,
      MemberType: MemberType,
      pageNo: pageNo,
      pageSize: pageSize,
      OverdueOnly: overdueOnly,
      FinePerDay: config.library_fine_per_day,
    };

    axios({
      method: "post",
      url: config.base_url + "Library/GetOpenIssuesReport",
      headers: { "Content-Type": "application/json" },
      data: data,
    })
      .then(function (response) {
        if (response.data.status_code === 1) {
          setListOfRecords(response.data.ListofRecords || []);
          setTotalRecords(response.data.totalRecords || 0);
        } else {
          setListOfRecords([]);
          setTotalRecords(0);
          if (response.data.status_message) {
            toast.error(response.data.status_message, { position: toast.POSITION.TOP_RIGHT });
          }
        }
        setLoading(false);
      })
      .catch(function () {
        setLoading(false);
        toast.error("Network Error..", { position: toast.POSITION.TOP_RIGHT });
      });
  }, [UserID, AccessKey, MemberType, pageNo, pageSize, reportMode, overdueOnly, refreshToken]);

  const calcFine = (item, onDate) => {
    if (!item.Return_Date) return 0;
    const due = moment(item.Return_Date).startOf("day");
    const actual = moment(onDate).startOf("day");
    if (actual.isAfter(due)) {
      return actual.diff(due, "days") * config.library_fine_per_day;
    }
    return 0;
  };

  const openReturn = (item) => {
    setSelectedItem(item);
    const today = moment().format(config.date_format_input);
    setReturnDate(today);
    setEstimatedFine(calcFine(item, today));
    setShowReturn(true);
  };

  const openRenew = (item) => {
    setSelectedItem(item);
    setRenewDate(
      item.Return_Date
        ? moment(item.Return_Date).format(config.date_format_input)
        : moment().add(7, "days").format(config.date_format_input)
    );
    setShowRenew(true);
  };

  const handleReturn = (event) => {
    event.preventDefault();
    const remarks = event.target.elements.Remarks.value;

    axios({
      method: "post",
      url: config.base_url + "Library/ReturnBook",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey: AccessKey,
        UserID: UserID,
        MemberType: MemberType,
        Book_ID: selectedItem.Book_ID,
        Actual_Return_Date: returnDate,
        Remarks: remarks,
        FinePerDay: config.library_fine_per_day,
      },
    })
      .then(function (response) {
        if (response.data.status_code === 1) {
          toast.success(response.data.status_message, { position: toast.POSITION.TOP_RIGHT });
          setShowReturn(false);
          setRefreshToken((t) => t + 1);
        } else {
          toast.error(response.data.status_message || "Failed to return book.", {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch(function () {
        toast.error("Network Error..", { position: toast.POSITION.TOP_RIGHT });
      });
  };

  const handleRenew = (event) => {
    event.preventDefault();
    if (!renewDate) {
      toast.error("Please select a new due date.", { position: toast.POSITION.TOP_RIGHT });
      return;
    }

    axios({
      method: "post",
      url: config.base_url + "Library/RenewBook",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey: AccessKey,
        UserID: UserID,
        MemberType: MemberType,
        Book_ID: selectedItem.Book_ID,
        Return_Date: renewDate,
      },
    })
      .then(function (response) {
        if (response.data.status_code === 1) {
          toast.success(response.data.status_message, { position: toast.POSITION.TOP_RIGHT });
          setShowRenew(false);
          setRefreshToken((t) => t + 1);
        } else {
          toast.error(response.data.status_message || "Failed to renew.", {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch(function () {
        toast.error("Network Error..", { position: toast.POSITION.TOP_RIGHT });
      });
  };

  const issuedTo = (item) => {
    if (item.Student_Name) {
      return `${item.Student_Name} (${item.Student_Registration || ""})`;
    }
    if (item.Employee_Name) {
      return `${item.Employee_Name} (${item.Employee_Code || ""})`;
    }
    return "";
  };

  const exportCsv = () => {
    if (!listOfRecords || listOfRecords.length === 0) {
      toast.error("No data to export.", { position: toast.POSITION.TOP_RIGHT });
      return;
    }
    csvDownload({
      data: listOfRecords.map((item) => ({
        Book_No: item.Book_No,
        Book_Name: item.Name_Of_Book,
        Issued_To: issuedTo(item),
        Issue_Date: item.Date_Of_Issue ? moment(item.Date_Of_Issue).format(config.date_format) : "",
        Due_Date: item.Return_Date ? moment(item.Return_Date).format(config.date_format) : "",
        Days_Overdue: item.DaysOverdue || 0,
        Estimated_Fine: item.EstimatedFine || 0,
        Mobile: item.Contact_Mobile || "",
        Remarks: item.Remarks || "",
      })),
      filename: overdueOnly ? "library-overdue" : "library-outstanding",
    });
  };

  const renderRows = () => {
    if (listOfRecords != null && listOfRecords.length > 0) {
      let count = 1;
      return listOfRecords.map((item, index) => (
        <tr key={index}>
          <td>{count++ + pageSize * (pageNo - 1)}</td>
          <td>{item.Book_No}</td>
          <td>{item.Name_Of_Book}</td>
          <td>{issuedTo(item)}</td>
          <td>{item.Date_Of_Issue ? moment(item.Date_Of_Issue).format("DD/MM/YYYY") : ""}</td>
          <td>{item.Return_Date ? moment(item.Return_Date).format("DD/MM/YYYY") : ""}</td>
          <td>{item.DaysOverdue > 0 ? item.DaysOverdue : "-"}</td>
          <td>{item.EstimatedFine > 0 ? item.EstimatedFine : "-"}</td>
          <td>{item.Contact_Mobile || "-"}</td>
          <td>
            <button type="button" className="button button-white btn-sm me-1" onClick={() => openRenew(item)}>
              Renew
            </button>
            <button type="button" className="button button-primary btn-sm" onClick={() => openReturn(item)}>
              Return
            </button>
          </td>
        </tr>
      ));
    }
    return (
      <tr>
        <td colSpan="10">
          <h4 className="mt-3" style={{ fontSize: "15px", color: "rgba(0,0,0,0.6)" }}>
            No Data Found
          </h4>
        </td>
      </tr>
    );
  };

  return (
    <>
      <LibraryMenu />
      <ToastContainer />
      <div className="page-head">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <NavLink to="/Dashboard">Dashboard</NavLink>
                  </li>
                  <li className="breadcrumb-item">
                    <NavLink to="/Library/LibraryDashboard">Library</NavLink>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {overdueOnly ? "Overdue Books" : "Outstanding Books"}
                  </li>
                </ol>
              </nav>
              <div className="page-title">{overdueOnly ? "Overdue Books" : "Outstanding Books"}</div>
            </div>
            <div className="col-md-6">
              <div className="page-right-actions">
                <NavLink to="/Library/ViewLibrary" className="button button-white button-icon">
                  Manage Library
                  <i className="ri-file-list-2-line"></i>
                </NavLink>
                <button
                  onClick={() => setShowFilters((v) => !v)}
                  className="button button-white button-icon"
                >
                  {showFilters ? "Hide Filters" : "Show Filters"}
                  <i className="ri-filter-3-line"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          <div className={`collapse ${showFilters ? "show" : ""}`}>
            <div className="card mb-2">
              <div className="card-body p-md-4">
                <div className="row align-items-end">
                  <div className="col-md-4">
                    <label>Report type</label>
                    <select
                      className="form-select"
                      value={reportMode}
                      onChange={(e) => {
                        setReportMode(e.target.value);
                        setPageNo(1);
                      }}
                    >
                      <option value="overdue">Overdue only (past due date)</option>
                      <option value="outstanding">All outstanding (not returned)</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <p className="mb-0 mt-3">
                      Fine rate: <strong>{config.library_fine_per_day}</strong> per day
                    </p>
                  </div>
                  <div className="col-md-4 text-end">
                    <span className="counting">{totalRecords} Records Found</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body table_card">
              <table className="table table-theme table-hover">
                <thead>
                  <tr>
                    <th>Sr.</th>
                    <th>Book No</th>
                    <th>Book Name</th>
                    <th>Issued To</th>
                    <th>Issue Date</th>
                    <th>Due Date</th>
                    <th>Days Overdue</th>
                    <th>Est. Fine</th>
                    <th>Mobile</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td className="text-center" colSpan="10">
                        <Spinner animation="border" role="status"></Spinner>
                      </td>
                    </tr>
                  ) : (
                    renderRows()
                  )}
                </tbody>
              </table>

              {totalRecords > pageSize && (
                <div className="pagination-wrap">
                  <div className="row align-items-center">
                    <div className="col-md-2 form_default">
                      <div className="d-flex align-items-center">
                        <label className="me-2" style={{ fontSize: "15px" }}>
                          Page Size
                        </label>
                        <select
                          className="form-select"
                          style={{ width: "100px" }}
                          value={pageSize}
                          onChange={(e) => {
                            setPageNo(1);
                            setPageSize(parseInt(e.target.value, 10));
                          }}
                        >
                          <option value="10">10</option>
                          <option value="20">20</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </select>
                      </div>
                    </div>
                    <Pagination
                      pageNo={pageNo}
                      pageSize={pageSize}
                      totalRecords={totalRecords}
                      onPageChange={setPageNo}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="card mt-md-3">
            <div className="card-body text-end">
              <button type="button" className="button button-primary" onClick={exportCsv}>
                Export CSV
              </button>
            </div>
          </div>
        </div>
      </div>

      <Modal className="theme-modal" show={showReturn} onHide={() => setShowReturn(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Return Book</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>{selectedItem.Name_Of_Book}</strong> — {issuedTo(selectedItem)}
          </p>
          <Form onSubmit={handleReturn}>
            <div className="form-group mb-3">
              <label>Return date</label>
              <input
                type="date"
                className="form-control"
                value={returnDate}
                onChange={(e) => {
                  setReturnDate(e.target.value);
                  setEstimatedFine(calcFine(selectedItem, e.target.value));
                }}
                required
              />
            </div>
            {estimatedFine > 0 && (
              <div className="alert alert-warning py-2">
                Late fine: <strong>{estimatedFine}</strong> ({config.library_fine_per_day}/day)
              </div>
            )}
            <div className="form-group mb-3">
              <label>Remarks</label>
              <textarea name="Remarks" className="form-control" required />
            </div>
            <button type="submit" className="button button-primary w-100">
              Submit Return
            </button>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal className="theme-modal" show={showRenew} onHide={() => setShowRenew(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Renew Due Date</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>{selectedItem.Name_Of_Book}</strong> — {issuedTo(selectedItem)}
          </p>
          <Form onSubmit={handleRenew}>
            <div className="form-group mb-3">
              <label>New due date</label>
              <input
                type="date"
                className="form-control"
                value={renewDate}
                onChange={(e) => setRenewDate(e.target.value)}
                min={moment().format(config.date_format_input)}
                required
              />
            </div>
            <button type="submit" className="button button-primary w-100">
              Save New Due Date
            </button>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default OverdueReport;
